import React, { useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { useParams, withRouter } from 'react-router-dom';
import moment from 'moment';
import AttachFile from '@mui/icons-material/AttachFile';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Download from '@mui/icons-material/Download';
import { getAll, deleteNote, getNoteFile, addNote, editNote } from '../../../../services/NoteSociete';
import FormNoteModal from '../../../../components/FormNoteModal/FormNoteModal';
import DialogDeleteCustom from '../../../../components/DialogDeleteCustom/DialogDeleteCustom';
import LabelReadMoreReadLess from '../../../../components/LabelReadMoreReadLess/LabelReadMoreReadLess';
import EditorCustom from '../../../../components/EditorCustorm/EditorCustom';
import { getUserData } from '../../../../utils/FunctionHelpers';
import { withSnackbar } from '../../../../utils/SnackbarHOC';
import { removeLineBreak } from '../../../../utils/EditorUtil';
import { commonGridSx, gridLocaleText, makeGridToolbar } from '../../../../utils/gridStyles';
import { DataGrid } from '../../../../components/SortableDataGrid/SortableDataGrid';

const CustomToolbar = makeGridToolbar('Notes');

const NoteSociete = ({ snackbarShowMessage }) => {
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [notesSociete, setNotesSociete] = useState([]);
  const [noteSocieteInfos, setNotesSocieteInfos] = useState({ auteur: '', id: '' });
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [loadingToDelete, setLoadingToDelete] = useState(false);
  const [reloadingData, setReloadingData] = useState(false);
  const [modalState, setModalState] = useState({ open: false });
  const [pj, setPj] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const active = useRef(false);
  const [textEdit, setTextEdit] = useState(true);
  const [idNoteSeleted, setIdNoteSeleted] = useState(null);
  const [downloadingRowIndex, setDownloadingRowIndex] = useState(null);
  const [dataUpload, setDataUpload] = useState({});
  const uploadRef = useRef(null);
  const [comment, setComment] = useState('');

  React.useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);

  React.useEffect(() => {
    let isActive = true;
    (async () => {
      setNotesSociete([]);
      setLoading(true);
      try {
        const res = await getAll(id, page + 1);
        if (!isActive) return;
        setNotesSociete(res.data.notesSociete || []);
        setRowCount(res.data.count || 0);
      } catch {
        snackbarShowMessage('Erreur de chargement des notes.', 'error', 4000);
      } finally {
        if (isActive) setLoading(false);
      }
    })();
    return () => { isActive = false; };
  }, [id, page, reloadingData]);

  const handleCloseDialog = () => setOpenDialogDelete(false);
  const handleOpenDialog = (row) => { setNotesSocieteInfos({ auteur: row.auteur, id: row.uuid }); setOpenDialogDelete(true); };

  const handleDownload = (noteId, fileName) => {
    const FileDownload = require('js-file-download');
    (async () => {
      setDownloadingRowIndex(noteId);
      try {
        snackbarShowMessage('Patientez svp!', 'warning', null);
        const response = await getNoteFile(fileName);
        if (!active.current) return;
        FileDownload(response.data, fileName);
        snackbarShowMessage('Bien téléchargé', 'success', 4000);
      } catch {
        snackbarShowMessage('Erreur de téléchargement du fichier', 'error', 4000);
      } finally {
        setDownloadingRowIndex(null);
      }
    })();
  };

  const handleDeleteNote = () => {
    (async () => {
      setLoadingToDelete(true);
      try {
        snackbarShowMessage('Patientez svp!', 'warning', null);
        const res = await deleteNote(id, noteSocieteInfos.id);
        snackbarShowMessage(res.data);
        handleCloseDialog();
        setReloadingData(!reloadingData);
      } catch {
        snackbarShowMessage('Erreur de suppression de la note.', 'error', 4000);
      } finally {
        setLoadingToDelete(false);
      }
    })();
  };

  const validate = (c) => {
    const temp = { commentaire: !(c?.replace(/<\/?[^>]+(>|$)/g, '') === '') ? '' : "Le champ 'COMMENTAIRE' est obligatoire." };
    setErrors(temp);
    return Object.values(temp).every((x) => x === '');
  };

  const handleOpen = () => setModalState({ open: true });
  const handleClose = () => { if (loading) return; setModalState({ open: false }); setComment(''); setErrors(''); };

  const handleSubmit = (c) => {
    if (validate(c)) {
      (async () => {
        setLoading(true);
        const formData = new FormData();
        formData.append('note', removeLineBreak(c));
        formData.append('auteur', getUserData().uuid);
        if (pj) formData.append('pj', pj);
        try {
          snackbarShowMessage('Patientez svp!', 'warning', null);
          const response = await addNote(id, formData);
          snackbarShowMessage(response.data);
          setReloadingData(!reloadingData);
          handleClose();
        } catch {
          snackbarShowMessage("Une erreur d'ajout est survenue.", 'error', 4000);
          handleClose();
        } finally {
          setLoading(false);
        }
      })();
    }
  };

  const handleUpdate = (data) => {
    (async () => {
      setLoading(true);
      let noteId = 0;
      const formData = new FormData();
      if (typeof data === 'string') {
        formData.append('note', removeLineBreak(data));
        formData.append('pj', pj);
      } else {
        const { comment: c, file, idNote } = data;
        formData.append('note', removeLineBreak(c));
        formData.append('pj', file);
        noteId = idNote;
      }
      try {
        snackbarShowMessage('Patientez svp!', 'warning', null);
        const response = await editNote(formData, id, idNoteSeleted ?? noteId);
        snackbarShowMessage(response.data);
        setReloadingData(!reloadingData);
        setTextEdit(true);
        handleClose();
      } catch {
        snackbarShowMessage('Une erreur de mise à jour est survenue.', 'error', 4000);
        handleClose();
      } finally {
        setLoading(false);
      }
    })();
  };

  const handleFileChange = (event) => handleUpdate({ comment: dataUpload[2], file: event.target.files[0], idNote: dataUpload[0] });
  const handleRowClick = (c, noteId) => { setComment(c); setIdNoteSeleted(noteId); setTextEdit(false); };

  const editorCustomProps = { value: comment, setValue: setComment, handleSubmit: handleUpdate, enableOnBlur: true };

  const columns = [
    {
      field: 'date', headerName: 'DATE', width: 110,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>
          {p.value?.date ? moment(p.value.date).format('DD/MM/YYYY') : '—'}
        </Typography>
      ),
    },
    {
      field: 'note', headerName: 'COMMENTAIRE', flex: 1, minWidth: 200, sortable: false,
      renderCell: (p) => (
        <Box sx={{ width: '100%', cursor: 'text' }}>
          {textEdit ? (
            <span onDoubleClick={() => handleRowClick(p.value, p.row.uuid)}>
              <LabelReadMoreReadLess data={p.value} />
            </span>
          ) : p.row.uuid === idNoteSeleted ? (
            <EditorCustom {...editorCustomProps} />
          ) : (
            <LabelReadMoreReadLess data={p.value} />
          )}
        </Box>
      ),
    },
    {
      field: 'auteur', headerName: 'AUTEUR', width: 130,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value || '—'}</Typography>,
    },
    {
      field: 'pj', headerName: 'P.J', width: 100, sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {p.value ? (
            <>
              <Tooltip title="Télécharger">
                <IconButton size="small" disabled={loading} onClick={() => handleDownload(p.row.uuid, p.value)}
                  sx={{ color: '#03407b', p: 0.5, borderRadius: 1 }}>
                  {downloadingRowIndex === p.row.uuid ? <CircularProgress size={16} /> : <Download sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Modifier PJ">
                <IconButton size="small" disabled={loading} onClick={() => { uploadRef.current.click(); setDataUpload([p.row.uuid, null, p.row.note]); }}
                  sx={{ color: '#94a3b8', p: 0.5, borderRadius: 1 }}>
                  <Edit sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Ajouter PJ">
              <IconButton size="small" disabled={loading} onClick={() => { uploadRef.current.click(); setDataUpload([p.row.uuid, null, p.row.note]); }}
                sx={{ color: '#03407b', p: 0.5, borderRadius: 1 }}>
                <AttachFile sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          <input type="file" onChange={handleFileChange} style={{ display: 'none' }} ref={uploadRef} />
        </Box>
      ),
    },
    {
      field: 'actions_col', headerName: 'ACTIONS', width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        <Tooltip title="Supprimer">
          <IconButton size="small" onClick={() => handleOpenDialog(p.row)}
            sx={{ color: '#ef4444', background: '#fef2f2', borderRadius: '8px', '&:hover': { background: '#fee2e2' }, p: 0.75 }}>
            <Delete sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const rows = (notesSociete || []).map((n) => ({ ...n, id: n.uuid }));

  return (
    <>
      <DialogDeleteCustom
        handleClose={handleCloseDialog} open={openDialogDelete} handleConfirm={handleDeleteNote}
        loading={loadingToDelete} label={`Voulez vous vraiment supprimer la note de ${noteSocieteInfos?.auteur}?`}
      />
      <FormNoteModal errors={errors} setPj={setPj} loading={loading} handleSubmit={handleSubmit}
        modalState={modalState.open} handleOpen={handleOpen} handleClose={handleClose} />
      <Box sx={{ borderRadius: 3, border: '1px solid #e2e8f0', height: '47vh', minHeight: 300, background: '#fff', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.04)' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.uuid}
          loading={loading}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{ page, pageSize: 50 }}
          onPaginationModelChange={(m) => setPage(m.page)}
          pageSizeOptions={[50]}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          showToolbar
          slots={{ toolbar: CustomToolbar }}
          slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
          localeText={gridLocaleText}
          sx={{ ...commonGridSx, '& .MuiDataGrid-cell': { py: 0.75, alignItems: 'flex-start' } }}
        />
      </Box>
    </>
  );
};

export default withSnackbar(withRouter(NoteSociete));

